import React, { useState } from 'react'
import { Button } from './ui/button'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { LayoutDashboard, FileText, Building2, Users, Settings, Package, BookTemplate, Mail, BarChart3, CreditCard, TrendingUp, Map, Megaphone, Download, Key, User, LogOut, Shield, ChevronRight, FileSignature } from 'lucide-react'
import NotificationCenter from './NotificationCenter'

const TopNavigation = ({ 
  onNavigate, 
  onSaveQuote, 
  onNewQuote,
  onLogout,
  salesCoachEnabled,
  onToggleSalesCoach
}) => {
  const [openDropdown, setOpenDropdown] = useState(null)

  const toggleDropdown = (name) => {
    setOpenDropdown(openDropdown === name ? null : name)
  }

  const handleNavigate = (type) => {
    onNavigate(type)
    setOpenDropdown(null)
  }

  return (
    <div className="bg-white shadow-sm border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo - linksbündig */}
          <div className="flex items-center">
            <div className="text-2xl font-bold tracking-tight text-slate-900">
              off<span style={{color: "#f59e0b"}}>f</span>er
            </div>
          </div>

          {/* Main Navigation - rechtsbündig */}
          <nav className="hidden md:flex items-center space-x-1">
              {/* Dashboard */}
              <Button
                variant="ghost"
                onClick={() => handleNavigate('dashboard')}
                className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 font-medium gap-1.5 text-sm"
              >
                <LayoutDashboard className="h-4 w-4" />
                Dashboard
              </Button>

              {/* Angebote */}
              <Button
                variant="ghost"
                onClick={() => handleNavigate('quotes')}
                className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 text-sm gap-1.5"
              >
                <FileText className="h-4 w-4" />
                Angebote
              </Button>

              {/* Firmen */}
              <Button
                variant="ghost"
                onClick={() => handleNavigate('companies')}
                className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 text-sm gap-1.5"
              >
                <Building2 className="h-4 w-4" />
                Firmen
              </Button>

              {/* Kunden */}
              <Button
                variant="ghost"
                onClick={() => handleNavigate('customers')}
                className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 text-sm gap-1.5"
              >
                <Users className="h-4 w-4" />
                Kunden
              </Button>

              {/* Dokumente */}
              <Button
                variant="ghost"
                onClick={() => handleNavigate('documents')}
                className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 text-sm gap-1.5"
              >
                <FileSignature className="h-4 w-4" />
                Dokumente
              </Button>

              {/* Einrichtung Dropdown */}
              <div className="relative">
                <Button
                  variant="ghost"
                  onClick={() => toggleDropdown('einrichtung')}
                  className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 text-sm gap-1.5"
                >
                  <Settings className="h-4 w-4" />
                  Einrichtung <ChevronDownIcon className="ml-1 h-4 w-4" />
                </Button>
                {openDropdown === 'einrichtung' && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-slate-200 py-1 z-50">
                    <button
                      onClick={() => handleNavigate('services')}
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                    >
                      Services
                    </button>
                    <button
                      onClick={() => handleNavigate('packages')}
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                    >
                      Pakete
                    </button>
                    <button
                      onClick={() => handleNavigate('templates')}
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                    >
                      Vorlagen
                    </button>
                    <button
                      onClick={() => handleNavigate('document-templates')}
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                    >
                      Rechtsdokumente
                    </button>
                    <button
                      onClick={() => handleNavigate('email-templates')}
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                    >
                      E-Mail-Vorlagen
                    </button>
                  </div>
                )}
              </div>

              {/* Finanztools Dropdown */}
              <div className="relative">
                <Button
                  variant="ghost"
                  onClick={() => toggleDropdown('finanztools')}
                  className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 text-sm gap-1.5"
                >
                  <BarChart3 className="h-4 w-4" /> Finanztools <ChevronDownIcon className="ml-1 h-4 w-4" />
                </Button>
                {openDropdown === 'finanztools' && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-slate-200 py-1 z-50">
                    <button
                      onClick={() => handleNavigate('revenue')}
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                    >
                      💵 Einnahmen
                    </button>
                    <button
                      onClick={() => handleNavigate('expenses')}
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                    >
                      Fixkosten
                    </button>
                    <button
                      onClick={() => handleNavigate('domains')}
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                    >
                      🌐 Domains
                    </button>
                    <button
                      onClick={() => handleNavigate('reconciliation')}
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                    >
                      🔍 Einnahmen-Abgleich
                    </button>
                    <button
                      onClick={() => handleNavigate('cashflow')}
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                    >
                      Cashflow
                    </button>
                    <hr className="my-1 border-slate-100" />
                    <button
                      onClick={() => handleNavigate('vertrieb')}
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                    >
                      🏆 Vertrieb & Provision
                    </button>
                  </div>
                )}
              </div>

              {/* Roadmap-Button entfernt — interne Dev-Notizen, nicht für Endnutzer */}

              {/* Sales-Coach Toggle */}
              {onToggleSalesCoach && (
                <div className="flex items-center gap-2 px-3">
                  <button
                    onClick={onToggleSalesCoach}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      salesCoachEnabled ? 'bg-slate-900' : 'bg-slate-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        salesCoachEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className="text-xs font-medium text-slate-700">Sales-Coach</span>
                </div>
              )}
              
              {/* Notification Center */}
              <NotificationCenter 
                onNavigateToQuote={(quoteId) => {
                  setOpenDropdown(null); // Close dropdowns when notification clicked
                  onNavigate('quotes');
                }}
                onOpen={() => setOpenDropdown(null)} // Close dropdowns when notifications open
              />

              {/* Account Dropdown */}
              <div className="relative">
                <Button
                  variant="ghost"
                  onClick={() => toggleDropdown('account')}
                  className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 text-sm gap-1.5"
                >
                  <User className="h-4 w-4" />
                  Account <ChevronDownIcon className="ml-1 h-4 w-4" />
                </Button>
                {openDropdown === 'account' && (
                  <div className="absolute top-full right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-slate-200 py-1 z-50">
                    <button
                      onClick={() => handleNavigate('data-export')}
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                    >
                      Export / Import
                    </button>
                    <button
                      onClick={() => handleNavigate('apikeys')}
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                    >
                      API-Schlüssel
                    </button>
                    <button
                      onClick={() => handleNavigate('account')}
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                    >
                      Account
                    </button>
                    <div className="border-t border-slate-200 mt-1">
                      <button
                        onClick={onLogout}
                        className="w-full text-left px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 flex items-center gap-2"
                      >
                        <LogOut className="h-4 w-4" />
                        Abmelden
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </nav>

        </div>
      </div>

      {/* Close dropdowns when clicking outside */}
      {openDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setOpenDropdown(null)}
        />
      )}
    </div>
  )
}

export default TopNavigation
